import React, { JSX, useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { useBoards } from "../contexts/BoardContext";
import AppLayout from "../components/layout/AppLayout";
import ViewEntryModal from "../components/boards/ViewEntryModal";
import BoardSettingsModal from "../components/boards/BoardSettingsModal";
import ConfirmationModal from "../components/common/ConfirmationModal";
import "./BoardPage.css";

type Entry = {
    id: string;
    user_id: string;
    board_id: string;
    title: string | null;
    content: string;
    image_url: string | null;
    reactions_count: number;
    user_has_reacted: boolean;
    created_at: string;
    // Joined from profiles
    author?: {
        display_name: string | null;
        avatar_url: string | null;
    };
};

type Board = {
    id: string;
    name: string;
    cover_image_url?: string;
    invite_code?: string;
    created_at: string;
};

type Member = {
    id: string;
    display_name: string;
    avatar_url?: string;
};

const POST_IT_COLORS = [
    'var(--post-it-yellow)',
    'var(--post-it-blue)',
    'var(--post-it-green)',
    'var(--post-it-pink)',
    'var(--post-it-purple)',
    'var(--post-it-orange)'
];

export default function BoardPage(): JSX.Element {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { boards, latestEntry, updateBoard, refreshBoards } = useBoards();
    const { boardId } = useParams<{ boardId: string }>();

    // Use cached board data from context for instant header render
    const cachedBoard = boards.find(b => b.id === boardId);

    const [currentDate, setCurrentDate] = useState(new Date());
    const [board, setBoard] = useState<Board | null>(
        cachedBoard ? { ...cachedBoard, cover_image_url: cachedBoard.cover_image_url || undefined, created_at: cachedBoard.created_at || '' } : null
    );
    const [entries, setEntries] = useState<Entry[]>([]);
    const [members, setMembers] = useState<Member[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
    const [togglingReaction, setTogglingReaction] = useState<string | null>(null);
    const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
    const [leaveLoading, setLeaveLoading] = useState(false);

    // Derived state for the currently selected entry
    const selectedEntry = selectedEntryId ? entries.find(e => e.id === selectedEntryId) || null : null;

    // Fetch entries for a specific date using the combined RPC
    const fetchEntriesForDate = useCallback(async (date: Date) => {
        if (!boardId) return [];

        // Get start and end of the selected date in LOCAL timezone
        const startOfDay = new Date(date);
        startOfDay.setHours(0, 0, 0, 0);

        const endOfDay = new Date(date);
        endOfDay.setHours(23, 59, 59, 999);

        const { data, error } = await supabase.rpc('get_board_entries_with_reactions', {
            p_board_id: boardId,
            p_user_id: user?.id || null,
            p_start_date: startOfDay.toISOString(),
            p_end_date: endOfDay.toISOString()
        });

        if (error) {
            console.error('Error fetching entries:', error);
            return [];
        }

        // Transform RPC result to match Entry type
        return (data || []).map((entry: any) => ({
            id: entry.id,
            user_id: entry.user_id,
            board_id: entry.board_id,
            title: entry.title,
            content: entry.content,
            image_url: entry.image_url,
            created_at: entry.created_at,
            reactions_count: Number(entry.reactions_count) || 0,
            user_has_reacted: entry.user_has_reacted || false,
            author: {
                display_name: entry.display_name,
                avatar_url: entry.avatar_url
            }
        }));
    }, [boardId, user?.id]);

    // Fetch board details (for manual refresh after settings update)
    const fetchBoard = useCallback(async () => {
        if (!boardId) return;

        const { data, error } = await supabase
            .from('boards')
            .select('id, name, cover_image_url, invite_code, created_at')
            .eq('id', boardId)
            .single();

        if (error) {
            console.error('Error fetching board:', error);
        } else {
            setBoard(data);
        }
    }, [boardId]);

    // Combined parallel fetch for board details and entries
    useEffect(() => {
        const loadBoardData = async () => {
            if (!boardId) return;

            setLoading(true);

            // Run board details and entries fetch IN PARALLEL
            const [boardResult, entriesData] = await Promise.all([
                supabase
                    .from('boards')
                    .select('id, name, cover_image_url, invite_code, created_at')
                    .eq('id', boardId)
                    .single(),
                fetchEntriesForDate(currentDate)
            ]);

            if (boardResult.error) {
                console.error('Error fetching board:', boardResult.error);
            } else {
                setBoard(boardResult.data);
            }

            setEntries(entriesData);
            setLoading(false);

            // Fire-and-forget prefetch for members (non-blocking)
            supabase
                .from('board_members')
                .select('user_id')
                .eq('board_id', boardId)
                .then(({ data: memberData }) => {
                    if (memberData && memberData.length > 0) {
                        const userIds = memberData.map(m => m.user_id);
                        supabase
                            .from('profiles')
                            .select('id, display_name, avatar_url')
                            .in('id', userIds)
                            .then(({ data: profilesData }) => {
                                if (profilesData) setMembers(profilesData);
                            });
                    }
                });
        };

        loadBoardData();
    }, [boardId, currentDate, fetchEntriesForDate]);

    // Optimistic / Local Update: Listen for new entries created globally
    useEffect(() => {
        if (latestEntry && latestEntry.board_id === boardId) {
            // Check if it's already in the list (to avoid dupes if fetchEntries ran fast)
            setEntries(prev => {
                const exists = prev.some(e => e.id === latestEntry.id);
                if (exists) return prev;
                // Prepend new entry, ensuring user_has_reacted is set
                const entryWithReaction = {
                    ...latestEntry,
                    user_has_reacted: latestEntry.user_has_reacted ?? false
                };
                return [entryWithReaction, ...prev];
            });
        }
    }, [latestEntry, boardId]);

    // Toggle reaction on an entry
    const handleToggleReaction = async (entryId: string, e: React.MouseEvent) => {
        e.stopPropagation(); // Don't open modal when clicking heart
        if (!user || togglingReaction) return;

        setTogglingReaction(entryId);

        // Get current state from the entry itself
        const originalEntry = entries.find(e => e.id === entryId);
        const hasReacted = originalEntry?.user_has_reacted || false;
        const originalCount = originalEntry?.reactions_count || 0;
        const newCount = hasReacted ? Math.max(0, originalCount - 1) : originalCount + 1;

        // Optimistic update: toggle both count and user_has_reacted on the entry
        setEntries(prev => prev.map(entry =>
            entry.id === entryId
                ? { ...entry, reactions_count: newCount, user_has_reacted: !hasReacted }
                : entry
        ));

        try {
            if (hasReacted) {
                // Remove reaction
                const { error } = await supabase
                    .from('entry_reactions')
                    .delete()
                    .eq('entry_id', entryId)
                    .eq('user_id', user.id);

                if (error) throw error;
            } else {
                // Add reaction
                const { error } = await supabase
                    .from('entry_reactions')
                    .insert({ entry_id: entryId, user_id: user.id });

                if (error) throw error;
            }

        } catch (error) {
            console.error('Error toggling reaction:', error);
            // Revert optimistic update on error
            setEntries(prev => prev.map(entry =>
                entry.id === entryId
                    ? { ...entry, reactions_count: originalCount, user_has_reacted: hasReacted }
                    : entry
            ));
        } finally {
            setTogglingReaction(null);
        }
    };

    const isToday = (date: Date) => {
        const today = new Date();
        return date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear();
    };

    const isFuture = (date: Date) => {
        const today = new Date();
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        const t = new Date(today);
        t.setHours(0, 0, 0, 0);
        return d > t;
    };

    // Check if a date is before (or on) the board's creation date
    const isBeforeOrAtCreation = (date: Date) => {
        if (!board?.created_at) return false;
        const creationDate = new Date(board.created_at);
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        creationDate.setHours(0, 0, 0, 0);
        return d <= creationDate;
    };

    const handlePrevDay = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() - 1);
        // Don't navigate before the board's creation date
        if (board?.created_at) {
            const creationDate = new Date(board.created_at);
            creationDate.setHours(0, 0, 0, 0);
            newDate.setHours(0, 0, 0, 0);
            if (newDate < creationDate) return;
        }
        setCurrentDate(newDate);
    };

    const handleNextDay = () => {
        const newDate = new Date(currentDate);
        newDate.setDate(currentDate.getDate() + 1);

        if (!isFuture(newDate)) {
            setCurrentDate(newDate);
        }
    };

    const formatDate = (date: Date) => {
        if (isToday(date)) return "Today";

        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        if (date.getDate() === yesterday.getDate() &&
            date.getMonth() === yesterday.getMonth() &&
            date.getFullYear() === yesterday.getFullYear()) {
            return "Yesterday";
        }

        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    };

    const handleConfirmLeave = async () => {
        if (!boardId || !user) return;

        try {
            setLeaveLoading(true);
            const { error } = await supabase
                .from('board_members')
                .delete()
                .eq('board_id', boardId)
                .eq('user_id', user.id);

            if (error) throw error;

            await refreshBoards(); // Refresh the global board list
            setShowLeaveConfirmation(false);
            setIsSettingsModalOpen(false);
            navigate('/app');

        } catch (error: any) {
            console.error('Error leaving board:', error);
            alert(`Failed to leave board: ${error.message || 'Unknown error'}`);
            setShowLeaveConfirmation(false);
        } finally {
            setLeaveLoading(false);
        }
    };

    const canGoNext = !isToday(currentDate);
    const canGoPrev = !isBeforeOrAtCreation(currentDate);

    return (
        <AppLayout>
            <div className="board-page">
                <div className="board-container">
                    {/* Header */}
                    <div className="board-header">
                        <button className="icon-button" onClick={() => navigate('/app')}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                        {board && (
                            <h1 className="board-title">{board.name}</h1>
                        )}

                        <button
                            className="icon-button"
                            onClick={() => setIsSettingsModalOpen(true)}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="3"></circle>
                                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                            </svg>
                        </button>
                    </div>

                    {/* Date Nav as a Pinned Note */}
                    <div className="date-nav">
                        <button className="icon-button" onClick={handlePrevDay} disabled={!canGoPrev}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                        <h2 className="current-date">
                            {formatDate(currentDate)}
                        </h2>
                        <button
                            className="icon-button"
                            onClick={handleNextDay}
                            disabled={!canGoNext}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>

                    {/* Content (Masonry Post-its) */}
                    <div className="entries-grid">
                        {loading ? (
                            // Blank while loading - entries will animate in
                            null
                        ) : entries.length === 0 ? (
                            <div className="entries-empty" style={{ columnSpan: 'all' } as React.CSSProperties}>
                                <p>No entries for {isToday(currentDate) || formatDate(currentDate) === "Yesterday" ? formatDate(currentDate).toLowerCase() : formatDate(currentDate)}. Share something you're grateful for!</p>
                            </div>
                        ) : (
                            entries.map((entry, index) => (
                                <div
                                    key={entry.id}
                                    className="entry-card"
                                    style={{
                                        backgroundColor: POST_IT_COLORS[index % POST_IT_COLORS.length]
                                    }}
                                    onClick={() => setSelectedEntryId(entry.id)}
                                >
                                    {/* Tape element - stops click propagation */}
                                    <div className="entry-tape" onClick={(e) => e.stopPropagation()} />

                                    {entry.image_url && (
                                        <img
                                            src={entry.image_url}
                                            alt=""
                                            className="entry-image"
                                            onLoad={(e) => e.currentTarget.classList.add('loaded')}
                                        />
                                    )}

                                    {entry.title && (
                                        <h3 className="entry-title">{entry.title}</h3>
                                    )}

                                    <p className="entry-body">{entry.content}</p>

                                    <div className="entry-footer">
                                        <div className="entry-author">
                                            {entry.author?.avatar_url ? (
                                                <img src={entry.author.avatar_url} alt={entry.author.display_name || ""} className="entry-avatar" />
                                            ) : (
                                                <div className="entry-avatar" style={{ background: 'linear-gradient(135deg, #ffd4b8 0%, #ffb088 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#ffffff', fontWeight: '700', fontFamily: 'var(--manrope-bold-font-family)' }}>
                                                    {entry.author?.display_name?.charAt(0) || '?'}
                                                </div>
                                            )}
                                            <span className="entry-author-name">{entry.author?.display_name || "Anonymous"}</span>
                                        </div>
                                        <button
                                            className={`heart-button ${entry.user_has_reacted ? 'active' : ''}`}
                                            onClick={(e) => handleToggleReaction(entry.id, e)}
                                            disabled={togglingReaction === entry.id}
                                        >
                                            <svg
                                                className={`heart-icon ${entry.user_has_reacted ? 'active' : ''}`}
                                                width="18"
                                                height="18"
                                                viewBox="0 0 24 24"
                                                fill={entry.user_has_reacted ? "currentColor" : "none"}
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                            </svg>
                                            {entry.reactions_count > 0 && (
                                                <span className="heart-count">{entry.reactions_count}</span>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <ViewEntryModal
                    open={selectedEntryId !== null && selectedEntry !== null}
                    onClose={() => setSelectedEntryId(null)}
                    entry={selectedEntry}
                    hasReacted={selectedEntry?.user_has_reacted || false}
                    onToggleReaction={(entryId) => {
                        // Create a synthetic event for the handler
                        handleToggleReaction(entryId, { stopPropagation: () => { } } as React.MouseEvent);
                    }}
                />

                {board && (
                    <BoardSettingsModal
                        open={isSettingsModalOpen}
                        onClose={() => setIsSettingsModalOpen(false)}
                        boardName={board.name}
                        currentCoverUrl={board.cover_image_url || null}
                        boardId={board.id}
                        inviteCode={board.invite_code || ''}
                        initialMembers={members}
                        onUpdate={(name, coverUrl) => {
                            fetchBoard();
                            if (boardId) updateBoard(boardId, { name, cover_image_url: coverUrl });
                        }}
                        onRequestLeave={() => setShowLeaveConfirmation(true)}
                    />
                )}

                <ConfirmationModal
                    open={showLeaveConfirmation}
                    onClose={() => setShowLeaveConfirmation(false)}
                    onConfirm={handleConfirmLeave}
                    title="Leave Board?"
                    message="Are you sure you want to leave this board? You will need an invite code to rejoin."
                    confirmText="Leave Board"
                    cancelText="Cancel"
                    confirmVariant="danger"
                    loading={leaveLoading}
                />
            </div>
        </AppLayout>
    );
}
